'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Calendar,
  Clock,
  BookOpen,
  Wrench,
  Package,
  ArrowLeftRight,
  LayoutGrid,
} from 'lucide-react';
import type { Article } from '@/lib/articles';

const CATEGORY_META: Record<string, { icon: typeof BookOpen; label: string }> = {
  Tutorial: { icon: BookOpen, label: 'Tutorials' },
  Engineering: { icon: Wrench, label: 'Engineering' },
  Product: { icon: Package, label: 'Product' },
  Comparison: { icon: ArrowLeftRight, label: 'Comparisons' },
};

export interface CategoryFilterProps {
  articles: Article[];
  categories: string[];
}

/**
 * Client-side tag filter for the homepage article grid.
 *
 * Receives the pre-computed category list and article array from the
 * server component and renders both the filter row and the (filtered)
 * cards grid. Extracted so the rest of the homepage — header, hero,
 * featured article, footer — can stay a server component.
 */
export function CategoryFilter({ articles, categories }: CategoryFilterProps) {
  const [active, setActive] = useState<string | null>(null);

  const filtered = active ? articles.filter((a) => a.tag === active) : articles;

  return (
    <>
      <nav className="blog-categories" aria-label="Filter articles by category">
        <button
          type="button"
          className={`blog-category-pill${active === null ? ' active' : ''}`}
          onClick={() => setActive(null)}
        >
          <LayoutGrid size={15} />
          All
        </button>
        {categories.map((cat) => {
          const meta = CATEGORY_META[cat];
          const Icon = meta?.icon ?? BookOpen;
          return (
            <button
              type="button"
              key={cat}
              className={`blog-category-pill${active === cat ? ' active' : ''}`}
              onClick={() => setActive(active === cat ? null : cat)}
            >
              <Icon size={15} />
              {meta?.label ?? cat}
            </button>
          );
        })}
      </nav>

      <section className="blog-listing">
        <div className="blog-listing-grid">
          {filtered.map((article, index) => (
            <Link key={article.slug} href={article.slug} className="blog-card">
              <Image
                src={
                  article.coverImage ??
                  `/api/placeholder/${encodeURIComponent(article.title)}`
                }
                alt={`${article.title} — cover image`}
                className="blog-card-cover"
                width={800}
                height={500}
                // First two cards are likely the LCP candidate above the
                // fold; preload them and lazy-load the rest.
                priority={index < 2}
                loading={index < 2 ? undefined : 'lazy'}
              />
              <div className="blog-card-body">
                <span className="blog-card-tag">{article.tag}</span>
                <h3 className="blog-card-title">{article.title}</h3>
                <p className="blog-card-excerpt">{article.description}</p>
                <div className="blog-card-meta">
                  <Calendar size={13} />
                  <span>{article.date}</span>
                  <span className="blog-meta-dot">&middot;</span>
                  <Clock size={13} />
                  <span>{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
